import { useEffect, useState } from "react";
import axios from "axios";
import { motion, useReducedMotion } from "framer-motion";
import { Clock, Loader2, Mail, MapPin, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { COMPANY, SERVICES } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO = [
  { icon: Phone, label: "Telefon", value: COMPANY.phone, href: COMPANY.phoneHref, testId: "contact-phone-link" },
  { icon: Mail, label: "E-Mail", value: COMPANY.email, href: COMPANY.emailHref, testId: "contact-email-link" },
  { icon: MapPin, label: "Einsatzgebiet", value: COMPANY.region, testId: "contact-region" },
  { icon: Clock, label: "Erreichbar", value: COMPANY.hours, testId: "contact-hours" },
];

export default function Contact({ prefill }) {
  const reduce = useReducedMotion();
  const [form, setForm] = useState({ name: "", email: "", plz: "", phone: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (prefill) setForm((f) => ({ ...f, message: prefill }));
  }, [prefill]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Vielen Dank! Ihre Anfrage ist bei uns eingegangen.", {
        description: "Wir melden uns in der Regel innerhalb von 24 Stunden.",
      });
      setForm({ name: "", email: "", plz: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Die Anfrage konnte nicht gesendet werden.", {
        description: "Bitte versuchen Sie es erneut oder rufen Sie uns direkt an.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: reduce ? 0 : 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5"
          >
            <span className="inline-block rounded-full border border-sky-200/70 bg-sky-50/80 px-3 py-1 font-mono text-xs uppercase tracking-[0.25em] text-sky-800">
              Kontakt
            </span>
            <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl">
              Sprechen wir über <em className="italic text-sky-700">Ihre Fenster.</em>
            </h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-600 sm:text-base">
              Beschreiben Sie kurz Ihr Anliegen – wir antworten mit einem verbindlichen Festpreis, in der Regel
              innerhalb von 24 Stunden.
            </p>

            <div className="mt-10 space-y-5">
              {INFO.map(({ icon: Icon, label, value, href, testId }) => (
                <div key={label} className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-sky-700 shadow-sm ring-1 ring-slate-200">
                    <Icon className="h-4.5 w-4.5" />
                  </div>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</p>
                    {href ? (
                      <a data-testid={testId} href={href} className="font-medium text-slate-900 hover:text-sky-700">
                        {value}
                      </a>
                    ) : (
                      <p data-testid={testId} className="font-medium text-slate-900">
                        {value}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            data-testid="contact-form"
            initial={{ opacity: 0, y: reduce ? 0 : 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-2xl border border-slate-200 bg-white p-8 shadow-[0_20px_50px_-30px_rgba(15,23,42,0.2)] sm:p-10 lg:col-span-7"
          >
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="contact-name">Ihr Name *</Label>
                <Input
                  id="contact-name"
                  data-testid="contact-input-name"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Max Mustermann"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-email">E-Mail *</Label>
                <Input
                  id="contact-email"
                  data-testid="contact-input-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="max@beispiel.de"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-phone">Telefon (optional)</Label>
                <Input
                  id="contact-phone"
                  data-testid="contact-input-phone"
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-plz">Postleitzahl *</Label>
                <Input
                  id="contact-plz"
                  data-testid="contact-input-plz"
                  required
                  inputMode="numeric"
                  pattern="[0-9]{5}"
                  minLength={5}
                  maxLength={5}
                  value={form.plz}
                  onChange={set("plz")}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Gewünschte Leistung</Label>
                <Select value={form.service} onValueChange={set("service")}>
                  <SelectTrigger data-testid="contact-select-service">
                    <SelectValue placeholder="Bitte wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s.id} value={s.title} data-testid={`contact-service-option-${s.id}`}>
                        {s.title}
                      </SelectItem>
                    ))}
                    <SelectItem value="Sonstiges" data-testid="contact-service-option-sonstiges">
                      Sonstiges
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-6 space-y-2">
              <Label htmlFor="contact-message">Ihre Nachricht *</Label>
              <Textarea
                id="contact-message"
                data-testid="contact-textarea-message"
                required
                minLength={5}
                rows={6}
                value={form.message}
                onChange={set("message")}
                placeholder="z. B. Anzahl der Fenster, Etage, Wunschtermin …"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-sky-700 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-sky-800 disabled:opacity-60 sm:w-auto"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {sending ? "Wird gesendet …" : "Anfrage senden"}
            </button>
            <p className="mt-4 text-xs leading-relaxed text-slate-500">
              Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Daten zur Bearbeitung der Anfrage zu. Details finden
              Sie in unserer Datenschutzerklärung.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
